import React, { Component, Fragment } from 'react';
import { compose, Query } from '../../reactApolloCompat';
import { withRouter } from '../../util/withRouter';
import { withSnackbarMessage } from '../../context/SnackbarMessage';
import withStyles from '@mui/styles/withStyles';
import Typography from '../../component/Typography';
import Paper from '@mui/material/Paper';
import { withErrorBoundary } from '@sentry/react';
import ErrorFallback from '../../component/ErrorFallback';
import Grid from '../../component/form/Grid';
import gql from 'graphql-tag';
import moment from 'moment';
import { Bar } from 'react-chartjs-2';
import {
    BarController,
    BarElement,
    CategoryScale,
    Chart as ChartJS,
    Legend,
    LinearScale,
    LineController,
    LineElement,
    PointElement,
    Tooltip
} from 'chart.js';

ChartJS.register(
    CategoryScale,
    LinearScale,
    BarElement,
    PointElement,
    LineElement,
    Tooltip,
    Legend,
    LineController,
    BarController
);

class Financial extends Component {
    render() {
        const { classes } = this.props;
        return (
            <Fragment>
                <Typography variant="display4" gutterBottom className={classes.pageTitle}>
                    <span>Financial</span>
                    <small className={classes.header}>Overview</small>
                </Typography>
                <Paper className={classes.paper} elevation={0}>
                    <Grid container spacing={3}>
                        <Grid item xs={12} sm={6}>
                            {this.renderInvoices()}
                        </Grid>
                        <Grid item xs={12} sm={6}>
                            {this.renderPayments()}
                        </Grid>
                    </Grid>
                    <Grid container spacing={3}>
                        <Grid item xs={12} className="metrics-page">
                            {this.renderCashflow()}
                        </Grid>
                    </Grid>
                </Paper>
            </Fragment>
        );
    }

    renderInvoices() {
        const { classes } = this.props;

        const query = gql`
            query readInvoices($dateBegin: String!, $dateUntil: String!, $isPurchase: Boolean!) {
                readInvoices(
                    limit: 1
                    offset: 0
                    filter: { IsPurchase: { eq: $isPurchase }, InvoiceDueDate: { gte: $dateBegin, lte: $dateUntil } }
                ) {
                    pageInfo {
                        totalCount
                    }
                }
            }
        `;

        const dateBegin = moment()
            .startOf('month')
            .format('YYYY-MM-DD');
        const dateUntil = moment()
            .endOf('month')
            .format('YYYY-MM-DD');
        return (
            <Grid container spacing={3}>
                <Grid item xs={6}>
                    <p className={classes.countAmount}>
                        Invoices
                        <br />
                        <small>due this month</small>
                        <br />
                        <Query query={query} variables={{ dateBegin, dateUntil, isPurchase: false }}>
                            {results => this.renderThing(results)}
                        </Query>
                    </p>
                </Grid>
                <Grid item xs={6}>
                    <p className={classes.countAmount}>
                        Bills
                        <br />
                        <small>due this month</small>
                        <br />
                        <Query query={query} variables={{ dateBegin, dateUntil, isPurchase: true }}>
                            {results => this.renderThing(results)}
                        </Query>
                    </p>
                </Grid>
            </Grid>
        );
    }

    renderPayments() {
        const { classes } = this.props;

        const query = gql`
            query readPayments($dateBegin: String!, $dateUntil: String!, $isPurchase: Boolean!) {
                readSalesRegisters(
                    limit: 1
                    offset: 0
                    filter: {
                        Invoice: { IsPurchase: { eq: $isPurchase } }
                        TransDate: { gte: $dateBegin, lte: $dateUntil }
                    }
                ) {
                    nodes {
                        ID
                        TransDate
                    }
                    pageInfo {
                        totalCount
                    }
                }
            }
        `;

        const dateBegin = moment()
            .startOf('month')
            .format('YYYY-MM-DD');
        const dateUntil = moment()
            .endOf('month')
            .format('YYYY-MM-DD');
        return (
            <Grid container spacing={3}>
                <Grid item xs={6}>
                    <p className={classes.countAmount}>
                        Receipts
                        <br />
                        <small>collected this month</small>
                        <br />
                        <Query query={query} variables={{ dateBegin, dateUntil, isPurchase: false }}>
                            {results => this.renderThing(results)}
                        </Query>
                    </p>
                </Grid>
                <Grid item xs={6}>
                    <p className={classes.countAmount}>
                        Payments
                        <br />
                        <small>sent this month</small>
                        <br />
                        <Query query={query} variables={{ dateBegin, dateUntil, isPurchase: true }}>
                            {results => this.renderThing(results)}
                        </Query>
                    </p>
                </Grid>
            </Grid>
        );
    }

    renderThing({ data, loading, error }) {
        const { pageInfo } = Object.values(data)[0] || {};
        if (loading) return 'Loading...';
        if (error) return 'Error!';
        return <span>{pageInfo.totalCount}</span>;
    }

    renderCashflow() {
        const myData = {
            labels: [],
            datasets: [
                {
                    spanGaps: true,
                    type: 'line',
                    label: 'Invoices',
                    data: [],
                    pointRadius: 6,
                    pointHoverRadius: 12,
                    borderColor: 'rgb(68 175 105)'
                },
                {
                    spanGaps: true,
                    type: 'line',
                    label: 'Bills',
                    data: [],
                    pointRadius: 6,
                    pointHoverRadius: 12,
                    borderColor: 'rgb(209 79 87)'
                },
                {
                    type: 'bar',
                    label: 'Receipts',
                    data: [],
                    pointStyle: 'rect',
                    borderColor: 'rgb(68 175 105)',
                    backgroundColor: 'rgba(68, 175, 105, 0.5)'
                },
                {
                    type: 'bar',
                    label: 'Payments',
                    data: [],
                    pointStyle: 'rect',
                    borderColor: 'rgb(209 79 87)',
                    backgroundColor: 'rgba(209, 79, 87, 0.5)'
                }
            ]
        };
        const formatter = new Intl.NumberFormat('en-AU', {
            style: 'currency',
            currency: 'AUD'
        });
        const options = {
            datasets: { bar: { maxBarThickness: 100 } },
            scales: {
                x: { grid: { offset: true } },
                y: { beginAtZero: true, ticks: { callback: label => formatter.format(label) } }
            }
        };

        const query = gql`
            query Cashflow($metricList: [MetricsRequest]!) {
                loadMetrics(metrics: $metricList) {
                    result
                }
            }
        `;
        return (
            <Fragment>
                <p>Cashflow (last six months)</p>
                <Query query={query} variables={{ metricList: [{ name: 'allCashflow' }] }} fetchPolicy="network-only">
                    {({ data, loading, error }) => {
                        const { loadMetrics } = (!error && data) || {};
                        const arrResult = (loadMetrics && JSON.parse(loadMetrics[0].result)) || [];
                        arrResult.reverse().forEach((element, index) => {
                            myData.labels.push(element.label);
                            const invoices = myData.datasets.find(obj => obj.label === 'Invoices');
                            invoices.data.push(element.invoiceTotal);
                            const bills = myData.datasets.find(obj => obj.label === 'Bills');
                            bills.data.push(element.billTotal);
                            const sent = myData.datasets.find(obj => obj.label === 'Payments');
                            sent.data.push(element.sentTotal);
                            const paid = myData.datasets.find(obj => obj.label === 'Receipts');
                            paid.data.push(element.paidTotal);
                        });

                        return (
                            <div style={{ width: '100%', textAlign: 'center' }}>
                                <Bar
                                    options={{
                                        ...options,
                                        maintainAspectRatio: false,
                                        plugins: {
                                            tooltip: {
                                                enabled: true,
                                                callbacks: {
                                                    label: context => {
                                                        let label = context.dataset.label || '';
                                                        if (label) {
                                                            label += ': ';
                                                        }
                                                        if (context.raw !== null) {
                                                            label += formatter.format(context.raw);
                                                        }
                                                        return label;
                                                    }
                                                }
                                            },
                                            legend: {
                                                position: 'bottom',
                                                labels: { usePointStyle: true, color: '#000000', font: { size: 16 } }
                                            }
                                        }
                                    }}
                                    height={400}
                                    data={myData}
                                    redraw={loading}
                                />
                            </div>
                        );
                    }}
                </Query>
            </Fragment>
        );
    }
}

const styles = ({ spacing, breakpoints, palette, funeralHome, typography }) => ({
    paper: {
        padding: `${spacing(2)} ${spacing(5)}`,
        [breakpoints.down('md')]: {
            padding: spacing(2)
        }
    },
    pageTitle: {
        color: palette.contentForeground[funeralHome]
    },
    header: {
        fontWeight: typography.fontWeightLight,
        marginLeft: 16,
        paddingLeft: 16,
        borderLeft: '1px solid ' + palette.action.active,
        color: palette.text.primary
    },
    countAmount: {
        textAlign: 'center',
        '& span': { fontSize: '4rem' }
    }
});

export default withErrorBoundary(compose(withRouter, withSnackbarMessage, withStyles(styles))(Financial), {
    fallback: ErrorFallback
});
